<template>
  <div class="row">
    <div class="col-12">
      <SectionTitle :has-line="true" :title="title" />
    </div>

    <div class="col-xs-12 col-sm-12">
      <DynamicFormList :items="requesters">
        <template #items="{ item, index }">
          <div class="col-12">
            <DynamicFormListItem
              :item="item"
              :no-actions="submitted"
              class="bg-light-gray border-radius--light"
            >
              <template #default="{ active, toggle }">
                <RequesterItem
                  :item="item"
                  :submitted="submitted || (keepSigned && !!item.id)"
                  :use-role="useRole"
                  :role-label="roleLabel"
                  cols="col-md col-12"
                >
                  <template
                    v-if="!(submitted || (keepSigned && !!item.id)) && !active"
                    #actions
                  >
                    <div class="col pt-2 col-md-2 col-lg-2 px-0 ml-auto">
                      <div
                        class="d-flex align-items-center justify-content-end h-100 w-100"
                      >
                        <MenuDropDown
                          title="Acciones"
                          :items="[
                            {
                              label: 'Editar',
                              icon: 'fas fa-edit fa-fw text-green',
                              border: 'bottom',
                              click: toggle,
                            },
                            {
                              label: 'Eliminar',
                              icon: 'fas fa-times-circle fa-fw text-red',
                              click: () => remove(index),
                            },
                          ]"
                        />
                      </div>
                    </div>
                  </template>
                </RequesterItem>
                <template v-if="!submitted && active">
                  <div
                    :style="{
                      'border-top': '1px solid #E2E2E2',
                      margin: '.5rem 0',
                    }"
                  />
                  <RequesterForm
                    :item="item"
                    :editting="active"
                    :catalog="{ positions }"
                    :cannot-edit-name="cannotEditName"
                    :use-role="useRole"
                    :role-label="roleLabel"
                    @submit="edit"
                    @cancel="toggle"
                  />
                </template>
              </template>
            </DynamicFormListItem>
          </div>
        </template>

        <template v-if="!submitted" #form="{ toggle }">
          <RequesterForm
            :catalog="{ positions }"
            :item="defaultItem"
            :cannot-edit-name="cannotEditName"
            :use-role="useRole"
            :role-label="roleLabel"
            @submit="add"
            @cancel="toggle"
          />
        </template>

        <template v-if="!submitted" #form-toggler="{ toggle }">
          <div>
            <template v-if="showAddButtom">
              <div class="text-right">
                <ActionButton
                  text="Añadir"
                  variant="success"
                  i="fas fa-file-plus"
                  @click="toggle"
                />
              </div>
            </template>
            <template v-if="!hasAnyItems && allowAddMore">
              <ActionButton
                type="activationButton"
                :text="principalButtonLabel"
                :class="{ invalid: validate }"
                @click="toggle"
              />
            </template>
            <CustomError
              v-if="validate"
              variant="no-border"
              :message="customErrorMsg"
            />
          </div>
        </template>
      </DynamicFormList>
    </div>

    <div v-if="submitted && !hasAnyItems" class="col-12">
      <div class="bg-light-gray border-radius--light py-4 text-center">
        No posee funcionarios firmantes
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import CustomError from 'custom-error';
import { DynamicFormList, DynamicFormListItem } from '@/components/common';
import MenuDropDown from 'dropdown';
import RequesterForm from './_components/RequesterForm';
import RequesterItem from './_components/RequesterItem';

export default {
  name: 'Requesters',
  components: {
    SectionTitle,
    CustomError,
    RequesterForm,
    RequesterItem,
    DynamicFormList,
    DynamicFormListItem,
    ActionButton,
    MenuDropDown,
  },
  props: {
    submitted: { type: Boolean, default: false },
    positionsList: { type: Array, default: () => [] },
    requesters: { type: Array, default: () => [] },
    cannotEditName: { type: Boolean, default: false },
    useRole: { type: Boolean, default: false },
    allowAddMore: { type: Boolean, default: true },
    validate: { type: Boolean, default: false },
    customErrorMsg: {
      type: String,
      default: 'Favor añadir la firma electrónica.',
      meta: {
        description: 'Mensaje de error encontrado',
        example: 'Firma requerida',
      },
    },
    title: {
      type: String,
      default: 'Firmas de funcionarios que solicitan la consulta',
    },
    principalButtonLabel: {
      type: String,
      default: 'Presione aquí para añadir firma de funcionario',
    },
    keepSigned: { type: Boolean, default: false },
    roleLabel: { type: String, default: 'Rol' },
    limitNewSignaturesTo: { type: [Number, String], default: 0 },
  },
  data: () => ({ activeForms: false }),
  computed: {
    hasAnyItems() {
      return this.requesters.length > 0;
    },
    newSignatures() {
      return this.requesters.filter((x) => !x.id);
    },
    hasAnyNewItems() {
      if (!this.keepSigned) return true;
      return this.newSignatures.length > 0;
    },
    positions() {
      return this.positionsList.map(
        ({ signaturePositionId, signaturePositionName }) => ({
          id: signaturePositionId,
          name: signaturePositionName,
        })
      );
    },
    showAddButtom() {
      if (
        +this.limitNewSignaturesTo > 0 &&
        this.allowAddMore &&
        this.keepSigned &&
        this.hasAnyNewItems
      ) {
        return this.newSignatures.length < +this.limitNewSignaturesTo;
      }
      return this.allowAddMore && this.hasAnyItems;
    },
    defaultItem() {
      return this.allowAddMore && !this.useRole
        ? {}
        : {
            name:
              this.userProfile.firstname +
              ' ' +
              this.userProfile.lastpaternalname,
            role: { name: this.userProfile.role, id: this.userProfile.roleId },
          };
    },
  },
  methods: {
    add(item, reset) {
      this.$emit('update:validate', false);
      if (this.submitted) {
        return;
      }
      this.requesters.push(item);
      reset();
    },
    edit(item, reset) {
      if (this.submitted) {
        return;
      }
      const index = this.requesters.findIndex((x) => x.id === item.id);
      this.requesters.splice(index, 1, item);
      reset();
    },
    remove(index) {
      if (this.submitted) {
        return;
      }
      this.requesters.splice(index, 1);
    },
  },
  watch: {
    requesters: {
      handler() {
        const value = this.keepSigned ? this.hasAnyNewItems : this.hasAnyItems;
        this.$emit('input', value);
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped>
::v-deep .invalid > button {
  background-color: #f7d7da;
  border: 1px solid #c5cdcf;
}
</style>
